import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import ProductCard from "@/components/ProductCard";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Search, SlidersHorizontal } from "lucide-react";

export default function Products() {
    const { t, lang } = useLang();
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [cities, setCities] = useState([]);
    const [q, setQ] = useState(searchParams.get("q") || "");
    const [category, setCategory] = useState(searchParams.get("category") || "all");
    const [city, setCity] = useState(searchParams.get("city") || "all");
    const [sort, setSort] = useState(searchParams.get("sort") || "recent");
    const [priceRange, setPriceRange] = useState([0, 1000]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data));
        api.get("/cities").then((r) => setCities(r.data));
    }, []);

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (q) params.q = q;
        if (category !== "all") params.category = category;
        if (city !== "all") params.city = city;
        if (sort) params.sort = sort;
        if (priceRange[0] > 0) params.min_price = priceRange[0];
        if (priceRange[1] < 1000) params.max_price = priceRange[1];
        api.get("/products", { params })
            .then((r) => setProducts(r.data))
            .finally(() => setLoading(false));
    }, [q, category, city, sort, priceRange]);

    return (
        <Layout>
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <span className="text-xs font-medium tracking-[0.2em] text-terracotta uppercase">
                            {t("appName")}
                        </span>
                        <h1 className="text-3xl md:text-4xl font-black text-espresso mt-1">{t("allProducts")}</h1>
                    </div>
                    <span className="text-sm text-muted-foreground" data-testid="products-count">
                        {products.length} {lang === "ar" ? "منتج" : "products"}
                    </span>
                </div>

                <div className="flex items-center bg-white border border-border rounded-full px-4 py-1.5 mb-6" data-testid="search-bar">
                    <Search className="w-4 h-4 text-muted-foreground me-2" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={t("search")}
                        className="flex-1 bg-transparent outline-none text-sm py-2"
                        data-testid="products-search-input"
                    />
                </div>

                <div className="grid md:grid-cols-4 gap-6">
                    <aside className="md:col-span-1 space-y-6" data-testid="filters-sidebar">
                        <div className="bg-white border border-border rounded-2xl p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <SlidersHorizontal className="w-4 h-4" />
                                <div className="font-bold text-sm">{lang === "ar" ? "الفلاتر" : "Filters"}</div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs font-bold text-muted-foreground mb-1.5 block">{t("filterByCategory")}</label>
                                    <Select value={category} onValueChange={setCategory}>
                                        <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{t("allCategories")}</SelectItem>
                                            {categories.map((c) => (
                                                <SelectItem key={c.category_id} value={c.slug}>
                                                    {lang === "ar" ? c.name_ar : c.name_en}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-muted-foreground mb-1.5 block">{t("filterByCity")}</label>
                                    <Select value={city} onValueChange={setCity}>
                                        <SelectTrigger data-testid="filter-city"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{t("allCities")}</SelectItem>
                                            {cities.map((c) => (
                                                <SelectItem key={c.city_id} value={c.slug}>
                                                    {lang === "ar" ? c.name_ar : c.name_en}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-muted-foreground mb-1.5 block">
                                        {t("filterByPrice")}: {priceRange[0]}—{priceRange[1]} {t("SAR")}
                                    </label>
                                    <Slider
                                        value={priceRange}
                                        onValueChange={setPriceRange}
                                        min={0}
                                        max={1000}
                                        step={10}
                                        data-testid="filter-price"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-muted-foreground mb-1.5 block">
                                        {lang === "ar" ? "الترتيب" : "Sort"}
                                    </label>
                                    <Select value={sort} onValueChange={setSort}>
                                        <SelectTrigger data-testid="filter-sort"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="recent">{t("sortRecent")}</SelectItem>
                                            <SelectItem value="popular">{t("sortPopular")}</SelectItem>
                                            <SelectItem value="price_asc">{t("sortPriceAsc")}</SelectItem>
                                            <SelectItem value="price_desc">{t("sortPriceDesc")}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    </aside>

                    <div className="md:col-span-3">
                        {loading ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="aspect-[3/4] rounded-2xl bg-surface animate-pulse" />
                                ))}
                            </div>
                        ) : products.length === 0 ? (
                            <div className="text-center py-16 text-muted-foreground" data-testid="no-results">{t("noResults")}</div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5" data-testid="products-grid">
                                {products.map((p) => (
                                    <ProductCard key={p.product_id} product={p} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}
